// MODULES
import { LotI, NFTI } from '@tacans/shared-marketplace-layer';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useMemo } from 'react';
import { useActionWithAuthorization } from 'src/features/common/collectible/containers/nft-actions/actions.hooks';
import { useAllLots } from 'src/features/common/collectible/hooks/collectible.hooks';
import { useGetActiveTokenTransaction } from 'src/features/common/collectible/hooks/useGetActiveTokenTransaction.hook';
import { MintNftModal } from 'src/features/common/collectible/modals/mint-nft-modal';
import { useGetUniverseTemplate } from 'src/hooks/use-get-universe-template';
// RESOURCES
import { useTokenTransaction } from 'src/hooks/use-token-transaction';
import { Button } from 'src/ui/white-labels/phoenxHolding/atoms/button';

interface MintCollectibleButtonI {
    nft: NFTI;
}

interface ILocale {
    mint: string;
    mintAndList: string;
}

export const NftMintAction = ({ nft }: MintCollectibleButtonI) => {
    const { t } = useTranslation(['actions']);
    const locale: ILocale = useMemo(
        () => ({
            mint: t('trade.mint', { ns: 'actions' }),
            mintAndList: t('trade.mintAndList', { ns: 'actions' }),
        }),
        [t]
    );

    const [lot] = useAllLots();
    const allLots = useAllLots() as LotI[];
    const activeTransactions = useGetActiveTokenTransaction();
    const { createTransaction, initialization } = useTokenTransaction();
    const withSale = Number(nft.copies) === 1;
    const { isNinjaTemplate, isOceanTemplate } = useGetUniverseTemplate();

    const mintCollectible = useCallback(async () => {
        MintNftModal.open({
            nft,
            lot: lot || allLots[0],
        });
    }, [nft, lot, allLots]);

    const createAndSaleCollectible = useCallback(async () => {
        const transaction = await createTransaction(nft, lot!);
        await transaction?.createAndSell();
    }, [createTransaction, nft, lot]);

    const createWithAuthorization = useActionWithAuthorization({
        callback: mintCollectible,
    });
    const createAndSaleWithAuthorization = useActionWithAuthorization({
        callback: createAndSaleCollectible,
    });

    const getButtonVariant = useCallback(() => {
        if (isNinjaTemplate) {
            return 'whiteRounded';
        }
        if (isOceanTemplate) {
            return 'o_white';
        }
        return 'white';
    }, [isNinjaTemplate, isOceanTemplate]);

    return (
        <>
            <Button
                onClick={createWithAuthorization}
                disabled={!!activeTransactions[0] || initialization}
                data-test-id="mint-nft-btn"
                size="lg"
            >
                {locale.mint}
            </Button>
            {withSale && (
                <Button
                    onClick={createAndSaleWithAuthorization}
                    disabled={!!activeTransactions[0] || initialization}
                    data-test-id="mint-list-nft-btn"
                    variant={getButtonVariant()}
                    size="lg"
                >
                    {locale.mintAndList}
                </Button>
            )}
        </>
    );
};